import * as React from "react";
import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { IconButton, Snackbar, SnackbarCloseReason } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DialogProps } from "@material-ui/core";
import { useGetSignupQuestionsQuery } from "redux/features/signupapi/signupApi";
import Address from "pages/home/form/Address";
import Nationality from "pages/home/form/Nationality";
import WorkExperience from "pages/home/form/WorkExperience";
import Finances from "pages/home/form/Finances";
import moment from "moment";

import {
  ClientItem,
  FormData,
  InputChangeHandler,
  KYC,
  TargetObject,
  ValidationErrors,
} from "pages/home/types";
import { useUpdateKycMutation } from "redux/features/userprofileapi/userProfileApi";
import { useMobile } from "hooks/useDisplay";

const KycDialog: React.FC<any> = ({ userInfoData }) => {
  const [scroll, setScroll] = useState<DialogProps["scroll"]>("paper");
  const [isModalClosable, setIsModalClosable] = useState<boolean>(false);
  const [defaultOpenModal, setDefaultOpenModal] = useState<boolean>(false);
  const [updateKyc, { isLoading: updatingKyc }] = useUpdateKycMutation();
  const removedFinancialValues = {
    ...userInfoData,
    kyc: {
      ...userInfoData?.kyc,
      financial: {
        ...userInfoData?.kyc?.financial,
        annual_income: "",
        asset_worth: "",
        fund_source: "",
      },
    },
  };
  const [formData, setFormData] = useState<FormData>(removedFinancialValues);

  const [successMessageOpen, setSuccessMessageOpen] = useState<boolean>(false);

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );

  const [employmentStatus, setEmploymentStatus] = useState<ClientItem[] | null>(
    null
  );
  const [employmentIndustry, setEmploymentIndustry] = useState<
    ClientItem[] | null
  >(null);
  const [annualIncome, setAnnualIncome] = useState<ClientItem[] | null>(null);
  const [assetWorth, setAssetWorth] = useState<ClientItem[] | null>(null);
  const [sourceOfFunds, setSourceOfFunds] = useState<ClientItem[] | null>(null);
  const mobile = useMobile();
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSuccessMessageOpen(false);
  };

  const transform = (
    source: FormData
  ): {
    "employment-status": {
      employment_status: string;
      employment_status_label: any;
    };
    "employment-industry": {
      employment_industry: string;
      employment_industry_label: any;
    };
    "job-role": { job_role: string; job_role_label: string };
    "annual-income": { annual_income: string; annual_income_label: any };
    "asset-worth": { asset_worth: string; asset_worth_label: any };
    "fund-account": {
      fund_source: string;
      fund_source_label: string;
    };
    address: {
      addr_street: string;
      addr_line_2: string;
      addr_city: string;
      addr_state: string;
      addr_zip: string;
      addr_country: string;
      au_state: string;
      it_province: string;
      za_province: string;
    };
    au_state: string;
    it_province: string;
    za_province: string;
  } => {
    const employmentStatusItem = employmentStatus?.find(
      (item) => item.value === source.kyc?.employment?.employment_status
    );
    const employmentIndustryItem = employmentIndustry?.find(
      (item) => item.value === source.kyc?.employment?.employment_industry
    );

    const employmentAnnualIncomeItem = annualIncome?.find(
      (item) => item.value === source.kyc?.financial?.annual_income
    );
    const employmentAssetWorthItem = assetWorth?.find(
      (item) => item.value === source.kyc?.financial?.asset_worth
    );
    const employmentSourceFundsItem = sourceOfFunds?.find(
      (item) => item.value === source.kyc?.financial?.source_funds
    );
    let isDevUrl = false;

    if (
      window.location.href.includes("https://dev.td365.finsatechnology.com")
    ) {
      isDevUrl = true;
    }

    return {
      "employment-status": {
        employment_status: source.kyc?.employment?.employment_status || "",
        employment_status_label:
          (employmentStatusItem && employmentStatusItem.label) || "",
      },
      "employment-industry": {
        employment_industry: source.kyc?.employment?.employment_industry || "",
        employment_industry_label:
          (employmentIndustryItem && employmentIndustryItem.label) || "",
      },
      "job-role": {
        job_role: source.kyc?.employment?.job_role || "",
        job_role_label: source.kyc?.employment?.job_role_label || "",
      },
      "annual-income": {
        annual_income: source.kyc?.financial?.annual_income || "",
        annual_income_label:
          (employmentAnnualIncomeItem && employmentAnnualIncomeItem.label) ||
          "",
      },
      "asset-worth": {
        asset_worth: source.kyc?.financial?.asset_worth || "",
        asset_worth_label:
          (employmentAssetWorthItem && employmentAssetWorthItem.label) || "",
      },
      "fund-account": {
        fund_source: source.kyc?.financial?.source_funds || "",
        fund_source_label:
          (employmentSourceFundsItem && employmentSourceFundsItem.label) || "",
      },
      address: {
        addr_street: source.kyc?.address?.addr_street || "",
        addr_line_2: source.kyc?.address?.addr_line_2 || "",
        addr_city: source.kyc?.address?.addr_city || "",
        addr_state: source.kyc?.address?.addr_state || "",
        addr_zip: source.kyc?.address?.addr_zip || "",
        addr_country: source.kyc?.address?.addr_country_id || "",
        au_state: source.kyc?.address?.au_state || "",
        it_province: source.kyc?.address?.it_province || "",
        za_province: source.kyc?.address?.za_province || "",
      },
      au_state: source.kyc?.address?.au_state || "",
      it_province: source.kyc?.address?.it_province || "",
      za_province: source.kyc?.address?.za_province || "",
      ...(isDevUrl || process.env.REACT_APP_WHITELABEL === "TD365UK"
        ? {
            "risk-acceptance": {
              risk_acceptance: isDevUrl
                ? false
                : source.kyc?.risk_acceptance?.confirmed ?? false,
            },
          }
        : {
            "risk-acceptance": {
              risk_acceptance: false,
            },
          }),
    };
  };
  const transformAddress = (address: any): any => {
    const transformedAddress = { ...address };

    if (address.addr_country === "IT" && address.it_province) {
      transformedAddress.province = address.it_province;
      delete transformedAddress.it_province;
    } else if (address.addr_country === "ZA" && address.za_province) {
      transformedAddress.province = address.za_province;
      delete transformedAddress.za_province;
    }

    return transformedAddress;
  };

  const transformAddr = (dta: any): FormData => {
    const transformedData = { ...dta };
    transformedData.address = transformAddress(dta.address);
    return transformedData;
  };

  useEffect(() => {
    if (userInfoData) {
      const status = userInfoData?.kyc?.status;
      const reminderSent = userInfoData?.kyc?.kyc_reminder_sent;
      const today = moment();
      const lastUpdatedDate = moment(userInfoData?.kyc?.last_updated_date);
      const daysSinceLastUpdate = today.diff(lastUpdatedDate, "days");
      if (
        daysSinceLastUpdate >= 0 &&
        daysSinceLastUpdate < 15 &&
        status === "KYC due"
      ) {
        setIsModalClosable(true);
        setDefaultOpenModal(true);
      } else if (daysSinceLastUpdate >= 30 && status === "KYC overdue") {
        setIsModalClosable(false);
        setDefaultOpenModal(true);
      } else if (daysSinceLastUpdate >= 25 && status === "KYC due") {
        setIsModalClosable(false);
        setDefaultOpenModal(true);
      } else if (daysSinceLastUpdate >= 15 && status === "KYC due") {
        if (reminderSent) {
          setIsModalClosable(false);
        } else {
          setIsModalClosable(true);
        }
        setDefaultOpenModal(true);
      } else if (status === "KYC overdue") {
        setIsModalClosable(false);
        setDefaultOpenModal(true);
      } else {
        setIsModalClosable(true);
        setDefaultOpenModal(false);
      }
      setFormData(removedFinancialValues);
    }
  }, [userInfoData]);

  const handleInputChange: InputChangeHandler = (
    section: keyof KYC,
    key: string,
    value: string,
    label?: string,
    selectedCountry?: string
  ) => {
    setFormData((prevData) => {
      const sectionData = prevData.kyc[section];
      const updatedSection = {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ...sectionData,
        [key]: value,
      };

      if (label !== undefined) {
        const labelKey = `${key}_label` as keyof typeof updatedSection;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        updatedSection[labelKey] = label;
      }

      return {
        ...prevData,
        kyc: {
          ...prevData.kyc,
          [section]: updatedSection,
        },
      };
    });
  };

  const { data: pageData, isFetching: stepFetching } =
    useGetSignupQuestionsQuery({
      step: `/signup/TD365UK/step2`,
    });

  useEffect(() => {
    if (!stepFetching && pageData && pageData.financial?.properties) {
      const { properties } = pageData.financial;

      setEmploymentStatus(
        properties.find((prop) => prop.key === "employment_status")?.options ||
          null
      );
      setEmploymentIndustry(
        properties.find((prop) => prop.key === "employment_industry")
          ?.options || null
      );
      setAnnualIncome(
        properties.find((prop) => prop.key === "approx_annual_income")
          ?.options || null
      );
      setAssetWorth(
        properties.find((prop) => prop.key === "approx_asset_value")?.options ||
          null
      );
      setSourceOfFunds(
        properties.find((prop) => prop.key === "source_funds")?.options || null
      );
    }
  }, [stepFetching, pageData]);

  const handleClose = () => {
    setDefaultOpenModal(false);
  };

  const validateFormData = (data: FormData): ValidationErrors => {
    const errors: ValidationErrors = {};
    const lowerCaseStatus =
      data.kyc?.employment?.employment_status?.toLowerCase();

    const exemptStatuses = ["unemployed", "student", "retired"];
    if (!exemptStatuses.includes(lowerCaseStatus)) {
      if (!data.kyc?.employment?.job_role) {
        errors.employment = "Job Role is required";
      }
      if (!data.kyc?.employment?.employment_industry) {
        errors.employment = "Employment Industry is required";
      }
    }
    if (!data.kyc?.address?.addr_street) {
      errors.address = "Street address is required";
    }
    if (!data.kyc?.financial?.annual_income) {
      errors.finances = "Annual income is required";
    }
    if (!data.kyc?.financial?.asset_worth) {
      errors.finances = "Asset worth is required";
    }
    if (!data.kyc?.financial?.source_funds) {
      errors.finances = "Source of funds is required";
    }
    const isAuState = data.kyc?.address?.addr_country_id === "AU";
    const isItProvince = data.kyc?.address?.addr_country_id === "IT";
    const isZAProvince = data.kyc?.address?.addr_country_id === "ZA";
    if (isAuState && !data.kyc?.address?.au_state) {
      errors.address = "State is required";
    }
    if (isItProvince && !data.kyc?.address?.it_province) {
      errors.address = "Province is required";
    }
    if (isZAProvince && !data.kyc?.address?.za_province) {
      errors.address = "Province is required";
    }

    return errors;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const errors = validateFormData(formData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    try {
      const mappedData = transform(formData);
      const body = transformAddr(mappedData);
      const res = await updateKyc({
        brandId: process.env.REACT_APP_WHITELABEL as string,
        body,
      }).unwrap();
      if (res === "ok") {
        setSuccessMessageOpen(true);
        handleClose();
      }
    } catch (error) {
      console.error("Failed to update KYC:", error);
    }
  };

  return (
    <>
      <Snackbar
        open={successMessageOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        message="Details successfully updated"
      />
      <Dialog
        open={defaultOpenModal}
        onClose={isModalClosable ? handleClose : () => null}
        maxWidth="lg"
        scroll={scroll}
        PaperProps={{
          sx: {
            width: !mobile ? "40%" : "100%",
            maxWidth: "none",
          },
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle
            sx={{ m: 0, p: 2, backgroundColor: "#242c35", color: "white" }}
            id="customized-dialog-title"
          >
            Confirm Details
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={isModalClosable ? handleClose : () => null}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 12,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon sx={{ marginLeft: "5px" }} />
          </IconButton>
          <DialogContent dividers>
            <div>
              <Address
                userInfoData={formData}
                onInputChange={handleInputChange}
                validationError={validationErrors.address}
              />
              <WorkExperience
                userInfoData={formData}
                employmentIndustry={employmentIndustry || []}
                employmentStatus={employmentStatus || []}
                onInputChange={handleInputChange}
                validationError={validationErrors.employment}
              />
              <Finances
                annualIncome={annualIncome || []}
                assetWorth={assetWorth || []}
                sourceFunds={sourceOfFunds || []}
                onInputChange={handleInputChange}
                formData={formData}
                userInfoData={formData}
                validationError={validationErrors.finances}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={isModalClosable ? handleClose : () => null}>
              Cancel
            </Button>
            <Button type="submit">
              {updatingKyc ? "Submitting..." : "Submit"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default KycDialog;
